import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { CraftableWeaponSummary } from "@/lib/crafting/types";
import { ammoLabel, assetUrl } from "@/lib/bungie/assets";

/**
 * Rarity accents.
 *
 * Unlike damage types — where a new subclass element can ship at any time, which
 * is why those icons come from the manifest — the tier list has been the same
 * five values for a decade. A small map is safe here, and it falls back to a
 * neutral border for anything unrecognised.
 */
const RARITY: Record<string, string> = {
  Exotic: "#ceae33",
  Legendary: "#b78df2",
  Rare: "#5076a3",
  Uncommon: "#366f42",
  Common: "#c3bcb4",
};

function accentFor(tierTypeName: string | undefined): string {
  return (tierTypeName && RARITY[tierTypeName]) || "var(--color-line-dim)";
}

export interface ItemTooltipProps {
  weapon: CraftableWeaponSummary;
  /** Where to open — the pointer, or a focused tile's corner. */
  initial: { x: number; y: number };
}

const OFFSET = 18;
const MARGIN = 12;

/**
 * The floating item tooltip, modelled on Destiny's inventory hover.
 *
 * Position is deliberately **not** React state. Following the pointer through
 * state re-rendered the whole catalogue — hundreds of tiles — on every mousemove,
 * which is what made this lag. Instead the element positions itself imperatively
 * via a transform, batched into an animation frame, so pointer movement costs no
 * React work at all. Only changing *which* weapon is hovered re-renders.
 */
export function ItemTooltip({ weapon, initial }: ItemTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const point = useRef(initial);
  const frame = useRef(0);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const { x, y } = point.current;
    // Flip to the other side of the cursor rather than letting it clip.
    const left =
      x + OFFSET + width + MARGIN > window.innerWidth
        ? Math.max(MARGIN, x - OFFSET - width)
        : x + OFFSET;
    const top =
      y + OFFSET + height + MARGIN > window.innerHeight
        ? Math.max(MARGIN, window.innerHeight - height - MARGIN)
        : y + OFFSET;
    el.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
  }, []);

  // Re-place when the weapon changes: a different name changes the width.
  useLayoutEffect(() => {
    point.current = initial;
    place();
  }, [weapon.recipeHash, initial, place]);

  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      point.current = { x: event.clientX, y: event.clientY };
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        place();
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [place]);

  const icon = assetUrl(weapon.icon);
  const element = assetUrl(weapon.damageTypeIcon);
  const ammo = ammoLabel(weapon.ammoType);
  const accent = accentFor(weapon.tierTypeName);

  return (
    <div
      ref={ref}
      role="tooltip"
      className="pointer-events-none fixed top-0 left-0 z-50 w-72 border border-line-dim bg-panel/97 shadow-xl will-change-transform"
      style={{ transform: `translate3d(${initial.x + OFFSET}px, ${initial.y + OFFSET}px, 0)` }}
    >
      <div aria-hidden className="h-0.5 w-full" style={{ background: accent }} />

      <div className="flex items-start justify-between gap-3 px-3 py-2">
        <div className="min-w-0">
          <h2 className="type-tooltip truncate text-base leading-tight">{weapon.name}</h2>
          <p className="mt-0.5 text-xs" style={{ color: accent }}>
            {weapon.tierTypeName}
          </p>
        </div>
        {icon && <img src={icon} alt="" aria-hidden className="h-9 w-9 shrink-0" />}
      </div>

      <div className="flex items-center gap-2 border-t border-line-dim px-3 py-2 text-xs text-ink-2">
        {element && <img src={element} alt="" aria-hidden className="h-4 w-4 shrink-0" />}
        <span>{weapon.damageTypeName}</span>
        <span aria-hidden className="text-ink-3">
          ·
        </span>
        <span>{weapon.itemTypeDisplayName}</span>
        {ammo && <span className="label-caps ml-auto text-[0.6rem] text-ink-3">{ammo}</span>}
      </div>

      <p className="label-caps border-t border-line-dim px-3 py-1.5 text-[0.6rem] text-ink-3">
        Click to shape →
      </p>
    </div>
  );
}
